import React, { useState } from "react";
import { UploadCloud, XCircle, FileText, Check, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function PDFUploader({ onUpload, uploading }) {
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const prevent = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDrop = (e) => {
    prevent(e);
    setDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile?.type === "application/pdf") {
      setFile(droppedFile);
    }
  };

  const handleChange = (e) => {
    const selected = e.target.files[0];
    if (selected?.type === "application/pdf") {
      setFile(selected);
    }
  };

  const handleUpload = () => {
    if (file && !uploading) {
      onUpload(file);
      // Simulate progress (in real app, this would be from actual upload progress)
      setUploadProgress(0);
    }
  };

  const removeFile = () => {
    if (!uploading) {
      setFile(null);
      setUploadProgress(0);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div
        onDragOver={(e) => {
          prevent(e);
          setDragging(true);
        }}
        onDragLeave={(e) => {
          prevent(e);
          setDragging(false);
        }}
        onDrop={onDrop}
        className={`relative overflow-hidden border-2 border-dashed rounded-xl p-10 w-full text-center transition-all duration-300 ${
          dragging
            ? "border-indigo-500 bg-gradient-to-br from-indigo-50 to-purple-50 scale-[1.02] shadow-lg"
            : "border-gray-300 bg-gradient-to-br from-white to-gray-50 hover:border-indigo-400 hover:shadow-md"
        }`}
      >
        {/* Animated background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <AnimatePresence mode="wait">
          {!file ? (
            <motion.div
              key="upload-prompt"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.2 }}
              className="relative z-10"
            >
              <motion.div
                animate={{
                  y: dragging ? [-5, 5, -5] : [0, -10, 0],
                }}
                transition={{
                  duration: dragging ? 0.5 : 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <UploadCloud className="mx-auto h-16 w-16 text-indigo-500 mb-4" />
              </motion.div>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                {dragging ? "Drop it like it's hot! 🔥" : "Upload Shipment Notes"}
              </h3>
              <p className="text-gray-600 mb-6 text-sm max-w-md mx-auto">
                Drag & drop your PDF shipment notes here, or click the button below to browse your files
              </p>

              <label className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg cursor-pointer hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 font-medium">
                <FileText className="w-5 h-5" />
                Choose PDF File
                <input
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={handleChange}
                  disabled={uploading}
                />
              </label>

              <p className="text-xs text-gray-500 mt-4">
                Supported format: PDF (Max 25MB)
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="file-preview"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative z-10"
            >
              <div className="bg-white rounded-xl p-6 shadow-lg max-w-md mx-auto border border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-pink-500 rounded-lg flex items-center justify-center shadow-md">
                      <FileText className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                      
                      {!uploading && (
                        <button
                          onClick={removeFile}
                          className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors p-1 hover:bg-red-50 rounded"
                          title="Remove file"
                        >
                          <XCircle className="w-5 h-5" />
                        </button>
                      )}
                    </div>

                    {uploading && (
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                          <span>Uploading...</span>
                          <span>{uploadProgress}%</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <motion.div
                            className="h-full bg-gradient-to-r from-indigo-500 to-purple-500"
                            initial={{ width: 0 }}
                            animate={{ width: `${uploadProgress}%` }}
                            transition={{ duration: 0.3 }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {!uploading && (
                  <motion.button
                    onClick={handleUpload}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-md hover:shadow-lg font-medium"
                  >
                    <Check className="w-5 h-5" />
                    Upload & Process
                  </motion.button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
